export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** A rotation. Kept normalized by everything here that produces one. */
export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/**
 * A 4x4 matrix in **column-major** order, the layout WGSL's `mat4x4f` expects.
 *
 * Column-major so a matrix can be written straight into a uniform buffer with
 * no transpose on the way. `m[12]`, `m[13]`, `m[14]` are the translation.
 */
export type Mat4 = Float32Array;

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const clamp3 = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp3 = (a: number, b: number, t: number): number => a + (b - a) * t;

export const addVec3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const subVec3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scaleVec3 = (v: Vec3, k: number): Vec3 => ({ x: v.x * k, y: v.y * k, z: v.z * k });
export const dot3 = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const length3 = (v: Vec3): number => Math.hypot(v.x, v.y, v.z);
export const distance3 = (a: Vec3, b: Vec3): number => Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);

export const cross3 = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

export function normalize3(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z);
  return len > 0 ? { x: v.x / len, y: v.y / len, z: v.z / len } : { x: 0, y: 0, z: 0 };
}

export const lerpVec3 = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
});

// --- quaternions -----------------------------------------------------------

export const quat = (x = 0, y = 0, z = 0, w = 1): Quat => ({ x, y, z, w });

export function quatFromAxisAngle(axis: Vec3, radians: number): Quat {
  const a = normalize3(axis);
  const half = radians / 2;
  const s = Math.sin(half);
  return { x: a.x * s, y: a.y * s, z: a.z * s, w: Math.cos(half) };
}

/** Yaw about y, then pitch about x, then roll about z — the usual camera order. */
export function quatFromEuler(pitch: number, yaw: number, roll = 0): Quat {
  const cp = Math.cos(pitch / 2);
  const sp = Math.sin(pitch / 2);
  const cy = Math.cos(yaw / 2);
  const sy = Math.sin(yaw / 2);
  const cr = Math.cos(roll / 2);
  const sr = Math.sin(roll / 2);
  return {
    x: sp * cy * cr + cp * sy * sr,
    y: cp * sy * cr - sp * cy * sr,
    z: cp * cy * sr - sp * sy * cr,
    w: cp * cy * cr + sp * sy * sr,
  };
}

export function multiplyQuat(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

export function normalizeQuat(q: Quat): Quat {
  const len = Math.hypot(q.x, q.y, q.z, q.w);
  return len > 0 ? { x: q.x / len, y: q.y / len, z: q.z / len, w: q.w / len } : quat();
}

export function rotateByQuat(v: Vec3, q: Quat): Vec3 {
  // v + 2 * cross(q.xyz, cross(q.xyz, v) + q.w * v) — the standard shortcut,
  // which avoids building a matrix to turn one vector.
  const qv = { x: q.x, y: q.y, z: q.z };
  const t = scaleVec3(cross3(qv, addVec3(cross3(qv, v), scaleVec3(v, q.w))), 2);
  return addVec3(v, t);
}

/**
 * Shortest-path interpolation between two rotations.
 *
 * Negates one input when they point apart, so turning from 350 to 10 degrees
 * goes the short way round rather than most of the way back.
 */
/**
 * Slerp written into an array, in components, allocating nothing.
 *
 * The awkward signature buys something specific: this runs once per rotating bone per frame — the busiest
 * loop in the engine on a scene full of animated characters — and the object-returning `slerp` below made
 * four short-lived quaternions every time it was called. Sampling a clip is a typed-array-to-typed-array
 * job everywhere else; this was the one step that left the arrays.
 */
export function slerpInto(
  out: Float32Array,
  at: number,
  ax: number,
  ay: number,
  az: number,
  aw: number,
  bx: number,
  by: number,
  bz: number,
  bw: number,
  t: number,
): void {
  let cosom = ax * bx + ay * by + az * bz + aw * bw;
  // The shorter of the two arcs. A quaternion and its negation are the same rotation, so flipping one is
  // free — and not flipping it sends the bone the long way round.
  if (cosom < 0) {
    cosom = -cosom;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let x: number;
  let y: number;
  let z: number;
  let w: number;
  if (cosom > 0.9995) {
    // Nearly parallel: lerp and renormalize, because sin(omega) goes to zero.
    x = ax + (bx - ax) * t;
    y = ay + (by - ay) * t;
    z = az + (bz - az) * t;
    w = aw + (bw - aw) * t;
  } else {
    const omega = Math.acos(cosom);
    const sinom = Math.sin(omega);
    const scale0 = Math.sin((1 - t) * omega) / sinom;
    const scale1 = Math.sin(t * omega) / sinom;
    x = ax * scale0 + bx * scale1;
    y = ay * scale0 + by * scale1;
    z = az * scale0 + bz * scale1;
    w = aw * scale0 + bw * scale1;
  }
  const len = Math.hypot(x, y, z, w);
  if (len > 0) {
    out[at] = x / len;
    out[at + 1] = y / len;
    out[at + 2] = z / len;
    out[at + 3] = w / len;
  } else {
    out[at] = 0;
    out[at + 1] = 0;
    out[at + 2] = 0;
    out[at + 3] = 1;
  }
}

const SLERP_SCRATCH = new Float32Array(4);

export function slerp(a: Quat, b: Quat, t: number): Quat {
  slerpInto(SLERP_SCRATCH, 0, a.x, a.y, a.z, a.w, b.x, b.y, b.z, b.w, t);
  return {
    x: SLERP_SCRATCH[0]!,
    y: SLERP_SCRATCH[1]!,
    z: SLERP_SCRATCH[2]!,
    w: SLERP_SCRATCH[3]!,
  };
}

// --- matrices --------------------------------------------------------------

export function mat4(): Mat4 {
  const m = new Float32Array(16);
  m[0] = 1;
  m[5] = 1;
  m[10] = 1;
  m[15] = 1;
  return m;
}

/** `out = a * b`, meaning "apply b, then a" to a column vector. */
export function multiplyMat4(a: Mat4, b: Mat4, out: Mat4 = mat4()): Mat4 {
  for (let column = 0; column < 4; column++) {
    const b0 = b[column * 4]!;
    const b1 = b[column * 4 + 1]!;
    const b2 = b[column * 4 + 2]!;
    const b3 = b[column * 4 + 3]!;
    out[column * 4] = a[0]! * b0 + a[4]! * b1 + a[8]! * b2 + a[12]! * b3;
    out[column * 4 + 1] = a[1]! * b0 + a[5]! * b1 + a[9]! * b2 + a[13]! * b3;
    out[column * 4 + 2] = a[2]! * b0 + a[6]! * b1 + a[10]! * b2 + a[14]! * b3;
    out[column * 4 + 3] = a[3]! * b0 + a[7]! * b1 + a[11]! * b2 + a[15]! * b3;
  }
  return out;
}

/** Translate, rotate, then scale — the order a scene node composes in. */
export function composeMat4(position: Vec3, rotation: Quat, scale: Vec3, out: Mat4 = mat4()): Mat4 {
  const { x, y, z, w } = rotation;
  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;

  out[0] = (1 - (yy + zz)) * scale.x;
  out[1] = (xy + wz) * scale.x;
  out[2] = (xz - wy) * scale.x;
  out[3] = 0;
  out[4] = (xy - wz) * scale.y;
  out[5] = (1 - (xx + zz)) * scale.y;
  out[6] = (yz + wx) * scale.y;
  out[7] = 0;
  out[8] = (xz + wy) * scale.z;
  out[9] = (yz - wx) * scale.z;
  out[10] = (1 - (xx + yy)) * scale.z;
  out[11] = 0;
  out[12] = position.x;
  out[13] = position.y;
  out[14] = position.z;
  out[15] = 1;
  return out;
}

export function transformPoint3(m: Mat4, p: Vec3): Vec3 {
  return {
    x: m[0]! * p.x + m[4]! * p.y + m[8]! * p.z + m[12]!,
    y: m[1]! * p.x + m[5]! * p.y + m[9]! * p.z + m[13]!,
    z: m[2]! * p.x + m[6]! * p.y + m[10]! * p.z + m[14]!,
  };
}

/** As `transformPoint3`, but ignoring translation — for directions and normals. */
export function transformDirection3(m: Mat4, v: Vec3): Vec3 {
  return {
    x: m[0]! * v.x + m[4]! * v.y + m[8]! * v.z,
    y: m[1]! * v.x + m[5]! * v.y + m[9]! * v.z,
    z: m[2]! * v.x + m[6]! * v.y + m[10]! * v.z,
  };
}

/** The general inverse, or the identity for a singular matrix. */
export function invertMat4(m: Mat4, out: Mat4 = mat4()): Mat4 {
  const a00 = m[0]!, a01 = m[1]!, a02 = m[2]!, a03 = m[3]!;
  const a10 = m[4]!, a11 = m[5]!, a12 = m[6]!, a13 = m[7]!;
  const a20 = m[8]!, a21 = m[9]!, a22 = m[10]!, a23 = m[11]!;
  const a30 = m[12]!, a31 = m[13]!, a32 = m[14]!, a33 = m[15]!;

  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;

  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  // A degenerate transform — a zero scale — is an ordinary thing for a game to
  // do, and a screen-to-world call is not the place to throw over it.
  if (det === 0 || !Number.isFinite(det)) return mat4();
  const d = 1 / det;

  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * d;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * d;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * d;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * d;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * d;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * d;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * d;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * d;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * d;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * d;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * d;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * d;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * d;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * d;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * d;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * d;
  return out;
}

/**
 * A right-handed perspective projection for WebGPU's 0..1 depth range.
 *
 * Not the OpenGL -1..1 convention: WebGPU clips z to 0..1, and using a GL
 * projection here puts half the scene behind the near plane. It is the single
 * commonest bug in a first WebGPU renderer.
 */
export function perspective(fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = far / (near - far);
  m[11] = -1;
  m[14] = (far * near) / (near - far);
  return m;
}

/** An orthographic projection, also for 0..1 depth. */
export function orthographic(
  left: number,
  right: number,
  bottom: number,
  top: number,
  near: number,
  far: number,
): Mat4 {
  const m = new Float32Array(16);
  m[0] = 2 / (right - left);
  m[5] = 2 / (top - bottom);
  m[10] = 1 / (near - far);
  m[12] = (right + left) / (left - right);
  m[13] = (top + bottom) / (bottom - top);
  m[14] = near / (near - far);
  m[15] = 1;
  return m;
}

/** A view matrix looking from `eye` at `target`. */
export function lookAt(eye: Vec3, target: Vec3, up: Vec3 = { x: 0, y: 1, z: 0 }): Mat4 {
  const forward = normalize3(subVec3(eye, target)); // +z points back at the eye
  let right = cross3(up, forward);
  // Looking straight up or down makes `up` parallel to forward and the cross
  // product vanish; nudge to a different up rather than emitting NaNs.
  if (length3(right) < 1e-6) {
    right = cross3({ x: 0, y: 0, z: 1 }, forward);
    if (length3(right) < 1e-6) right = { x: 1, y: 0, z: 0 };
  }
  right = normalize3(right);
  const trueUp = cross3(forward, right);

  const m = new Float32Array(16);
  m[0] = right.x;
  m[1] = trueUp.x;
  m[2] = forward.x;
  m[4] = right.y;
  m[5] = trueUp.y;
  m[6] = forward.y;
  m[8] = right.z;
  m[9] = trueUp.z;
  m[10] = forward.z;
  m[12] = -dot3(right, eye);
  m[13] = -dot3(trueUp, eye);
  m[14] = -dot3(forward, eye);
  m[15] = 1;
  return m;
}

/** An axis-aligned box in 3D. Used for bounds, culling and collision. */
export interface Box3 {
  min: Vec3;
  max: Vec3;
}

export const boxOf = (min: Vec3, max: Vec3): Box3 => ({ min, max });

export function boxesOverlap(a: Box3, b: Box3): boolean {
  return (
    a.min.x < b.max.x &&
    a.max.x > b.min.x &&
    a.min.y < b.max.y &&
    a.max.y > b.min.y &&
    a.min.z < b.max.z &&
    a.max.z > b.min.z
  );
}

export function boxCenter(b: Box3): Vec3 {
  return { x: (b.min.x + b.max.x) / 2, y: (b.min.y + b.max.y) / 2, z: (b.min.z + b.max.z) / 2 };
}

/**
 * Six clipping planes, packed `[a, b, c, d]` each, in the order left, right, bottom, top, near, far.
 *
 * A point is inside the volume when `a*x + b*y + c*z + d >= 0` for all six. One flat array rather than
 * six objects because this is tested against every drawable in the scene, every frame.
 */
export type Frustum = Float32Array;

/**
 * The view frustum of a view-projection matrix, as six planes.
 *
 * The planes fall straight out of the matrix (Gribb & Hartmann): a vertex is inside the volume when its
 * clip coordinates satisfy `-w <= x <= w`, `-w <= y <= w` and `0 <= z <= w` — WebGPU's depth range, which
 * is why the near plane here is row 2 alone and not `w + z` as it would be under OpenGL's -1..1. Writing
 * each of those inequalities out in terms of the rows of the matrix gives the plane, because each clip
 * coordinate is just a row dotted with the world-space point.
 *
 * The planes are NOT normalized. Nothing here needs a true distance — only the sign — and normalizing
 * would cost a square root per plane to produce the same six answers.
 */
export function frustumFromMatrix(m: Mat4, out: Frustum = new Float32Array(24)): Frustum {
  // Column-major storage, so row r of the matrix is m[r], m[4 + r], m[8 + r], m[12 + r].
  const set = (i: number, r: number, sign: number, w: number): void => {
    out[i] = sign * m[r]! + w * m[3]!;
    out[i + 1] = sign * m[4 + r]! + w * m[7]!;
    out[i + 2] = sign * m[8 + r]! + w * m[11]!;
    out[i + 3] = sign * m[12 + r]! + w * m[15]!;
  };
  set(0, 0, 1, 1); //  w + x >= 0   left
  set(4, 0, -1, 1); //  w - x >= 0   right
  set(8, 1, 1, 1); //  w + y >= 0   bottom
  set(12, 1, -1, 1); //  w - y >= 0   top
  set(16, 2, 1, 0); //      z >= 0   near
  set(20, 2, -1, 1); //  w - z >= 0   far
  return out;
}

/**
 * Whether any part of `b` could be inside `f`.
 *
 * Conservative in the way culling must be: it tests each plane against the box corner FARTHEST along that
 * plane's normal, so a box is rejected only when the whole box is behind a single plane. That misses the
 * rare box which is outside the frustum but not outside any one plane (a big box straddling a corner),
 * which draws something invisible — the harmless direction to be wrong in.
 */
/**
 * The squared distance from `p` to the nearest point of `b`, and 0 when it is inside.
 *
 * For "is this within a light's range" — a sphere-versus-box test that needs no square root, which is
 * the cull a local light's shadow pass runs over every caster in the scene, six times a frame.
 */
export function boxDistanceSquared(b: Box3, p: Vec3): number {
  const dx = Math.max(b.min.x - p.x, 0, p.x - b.max.x);
  const dy = Math.max(b.min.y - p.y, 0, p.y - b.max.y);
  const dz = Math.max(b.min.z - p.z, 0, p.z - b.max.z);
  return dx * dx + dy * dy + dz * dz;
}

export function boxInFrustum(b: Box3, f: Frustum): boolean {
  for (let i = 0; i < 24; i += 4) {
    const a = f[i]!;
    const c = f[i + 1]!;
    const d = f[i + 2]!;
    const px = a >= 0 ? b.max.x : b.min.x;
    const py = c >= 0 ? b.max.y : b.min.y;
    const pz = d >= 0 ? b.max.z : b.min.z;
    if (a * px + c * py + d * pz + f[i + 3]! < 0) return false;
  }
  return true;
}

/**
 * The box enclosing `b` once `m` has been applied to its eight corners.
 *
 * Pass `out` to write into an existing box instead of making one. The renderer does, because this runs
 * once per drawable per frame and the corners it walks were ten short-lived objects a call.
 */
export function transformBox(m: Mat4, b: Box3, out?: Box3): Box3 {
  const box = out ?? { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } };
  const min = box.min;
  const max = box.max;
  min.x = min.y = min.z = Infinity;
  max.x = max.y = max.z = -Infinity;
  // Column-major, so the basis vectors are columns 0..2 and the translation is column 3.
  for (let i = 0; i < 8; i++) {
    const cx = i & 1 ? b.max.x : b.min.x;
    const cy = i & 2 ? b.max.y : b.min.y;
    const cz = i & 4 ? b.max.z : b.min.z;
    const x = m[0]! * cx + m[4]! * cy + m[8]! * cz + m[12]!;
    const y = m[1]! * cx + m[5]! * cy + m[9]! * cz + m[13]!;
    const z = m[2]! * cx + m[6]! * cy + m[10]! * cz + m[14]!;
    if (x < min.x) min.x = x;
    if (y < min.y) min.y = y;
    if (z < min.z) min.z = z;
    if (x > max.x) max.x = x;
    if (y > max.y) max.y = y;
    if (z > max.z) max.z = z;
  }
  return box;
}
